'use client'

import ChevronLeft from '@mui/icons-material/ChevronLeft'
import ChevronRight from '@mui/icons-material/ChevronRight'
import { Box, IconButton, useTheme } from '@mui/material'
import type { SxProps, Theme } from '@mui/material/styles'

import React from 'react'

const DEFAULT_WIDTH = 280
const PRIMARY_NAV_HEIGHT = 80

export type SidebarShellProps = {
  width?: number
  collapsedWidth?: number
  collapsed?: boolean
  onToggleCollapsed?: () => void
  sticky?: boolean
  stickyOffset?: number
  bordered?: boolean
  ariaLabel?: string
  sx?: SxProps<Theme>
  children: React.ReactNode
}

const SidebarShell: React.FC<SidebarShellProps> = ({
  width = DEFAULT_WIDTH,
  collapsedWidth,
  collapsed = false,
  onToggleCollapsed,
  sticky = true,
  stickyOffset = PRIMARY_NAV_HEIGHT,
  bordered = true,
  ariaLabel = 'Sidebar',
  sx,
  children,
}) => {
  const theme = useTheme()
  const collapsible = collapsedWidth !== undefined
  const isCollapsed = collapsible && collapsed
  const effectiveWidth = isCollapsed ? collapsedWidth : width

  return (
    <Box
      component="nav"
      aria-label={ariaLabel}
      sx={[
        (t) => ({
          width: effectiveWidth,
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.paper',
          ...(bordered && { borderRight: `1px solid ${t.palette.background.subtle}` }),
          ...(sticky
            ? {
                position: 'sticky',
                top: stickyOffset,
                alignSelf: 'flex-start',
                height: `calc(100vh - ${stickyOffset}px)`,
              }
            : {
                height: '100%',
              }),
          transition: theme.transitions.create('width', {
            duration: theme.transitions.duration.shorter,
          }),
        }),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}>
      {children}
      {onToggleCollapsed && (
        <IconButton
          onClick={onToggleCollapsed}
          aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          size="small"
          sx={(t) => ({
            position: 'absolute',
            top: '50%',
            right: 0,
            transform: 'translate(50%, -50%)',
            bgcolor: 'background.paper',
            border: `1px solid ${t.palette.background.subtle}`,
            '&:hover': { bgcolor: 'background.paper' },
            zIndex: 2,
          })}>
          {isCollapsed ? <ChevronRight /> : <ChevronLeft />}
        </IconButton>
      )}
    </Box>
  )
}

export default SidebarShell
export { SidebarShell }
